import { type HomeyAPI } from 'homey-api';
import { shallow } from 'zustand/shallow';

import { BaseApiStore, FetchArgs } from '../../../store/BaseApiStore.ts';

import { wait } from '../../../lib/wait';

import { useAttach } from '../../../store/useAttach';
import { selectBase } from '../../../store/selectors.ts';

export interface State {
  optionLocationData: { value: Location } | null;
  optionModeData: { value: Mode | null } | null;
  isLoading: boolean;
  error: unknown;
}

type Location = {
  latitude: number;
  longitude: number;
  accuracy: number;
};

type Mode = 'manual' | 'auto';

export type LocationSettingsStoreState = State;

class LocationSettingsStore extends BaseApiStore<State> {
  constructor() {
    super('location-settings');
  }

  override createInitialState() {
    return {
      optionLocationData: null,
      optionModeData: null,
      isLoading: true,
      error: null,
    };
  }

  override async fetch({ api, silent, cache, abortController }: FetchArgs) {
    if (silent === false) {
      this.set(this.createInitialState());
    }

    try {
      const time = silent === true ? 0 : 0;
      const waitPromise = wait(time);

      const optionLocationPromise = api.geolocation.getOptionLocation();
      const optionModePromise = api.geolocation.getOptionMode?.().catch((error: unknown) => {
        // Only exists on <=2019 models.
        if (typeof error === 'object' && error !== null && 'code' in error && error.code === 404) {
          return { value: null };
        }

        throw error;
      });

      const [, optionLocationData, optionModeData] = await Promise.all([
        waitPromise,
        optionLocationPromise,
        optionModePromise,
      ]);

      if (abortController.signal.aborted) {
        return;
      }

      this.set({
        ...this.createInitialState(),
        optionLocationData: optionLocationData as unknown as { value: Location },
        optionModeData: optionModeData,
        isLoading: false,
      });
    } catch (error) {
      if (abortController.signal.aborted) return;

      this.set({
        ...this.createInitialState(),
        isLoading: false,
        error,
      });
    }
  }

  override destroy() {}

  override onAttached({ api }: { api: HomeyAPI }): void {}

  override onDetached({ api }: { api: HomeyAPI }): void {}

  use() {
    // eslint-disable-next-line react-hooks/rules-of-hooks
    useAttach(this, this.key);

    return { locationSettings: this.store(selectAll, shallow) };
  }

  async setOptionLocation({ value }: { value: Location }) {
    const api = this.getApi();
    const promise = api.geolocation.setOptionLocation({ value }) as unknown as Promise<{
      value: Location;
    }>;
    const result = await promise;
    this.set({ optionLocationData: result });
  }

  async setOptionMode({ value }: { value: Mode }) {
    const api = this.getApi();
    const promise = api.geolocation.setOptionMode({ value });
    const result = await promise;
    this.set({ optionModeData: result });
  }
}

function selectAll(state: LocationSettingsStoreState) {
  return {
    ...selectBase(state),
    optionLocationData: state.optionLocationData,
    optionModeData: state.optionModeData,
  };
}

export const locationSettingsStore = new LocationSettingsStore();
